// @ts-nocheck
/* eslint-disable */
import type { Reducer } from 'react';
import type { ApolloError } from '@apollo/client';
import type { LocaleServiceConfig } from '@volkswagen-onehub/locale-service';
import type { DealerContextServiceV1 } from '@oneaudi/dealer-context-service';
import type { Coordinates } from '@volkswagen-onehub/audi-feature-service-visitor-locator';
import type GoogleMapReact from 'google-map-react';
import type { Dealer } from '../../models/dealer';
import { defaultState } from '../map-context';
import type { DistanceUnit } from '../../models/common';
import type { ctaBarButtonConfigProps } from '../../components/map/PartnerBusinessCard/pbc-types';
import type {
  DealerReview,
  MyAudiBarButtonConfig,
  RatingSummary,
  ReviewCategory,
  ReviewSort,
  StarFilter,
} from '../../types';

export type State = {
  query: string;
  marketGoogleKey: string;
  devMode: boolean;
  pbcVersion: string;
  locale: Pick<LocaleServiceConfig, 'countryCode' | 'language'>;
  intlSearchEnabled: boolean;
  baseUrl: string;
  results: Dealer[] | null;
  dealerRatings: { [key: string]: number };
  dealerReviews: DealerReview[] | null;
  ratingSummary: RatingSummary | null;
  reviewCategoryFilter: ReviewCategory;
  reviewStarFilter: StarFilter;
  reviewSort: ReviewSort;
  isFlyoutOpen: boolean;
  hoveredDealer: Dealer | null;
  isGoogleMapsApiLoaded: boolean;
  currentResultsAvailableFilters: string[];
  authorDefinedFilters: string[];
  userSelectedFilters: string[];
  preselectedFilters: string[];
  isSidebarOpen: boolean;
  isAutocompletionOpen: boolean;
  isFiltersOverlayOpen: boolean;
  searchText: string;
  searchRadius: number;
  searchRadiusOptions: number[];
  initialMapZoomLevel: string;
  showAddress: boolean;
  isLoading: boolean;
  searchError: ApolloError | null;
  northEastPosition: Coordinates;
  southWestPosition: Coordinates;
  searchGeoLocation: Coordinates;
  placeId: string;
  isUserLocationError: boolean;
  isUserLocationPermissionGiven: boolean;
  isMapTriggeredByTeaser: boolean;
  showLocationError: boolean;
  updateMapZoomLevel: boolean;
  selectedDealerId: string | null;
  showRadius: boolean;
  searchRadiusExtended: boolean;
  isUserPositionUsed: boolean;
  groupMarkers: boolean;
  activeMobileView: 'map' | 'list';
  lastSubmittedSearch: null | string;
  mapZoom: number;
  searchInThisAreaVisible: boolean;
  bounds: GoogleMapReact.Bounds;
  mapCenterCoordinates: Coordinates;
  userGeolocation: Coordinates | null;
  distanceUnit: DistanceUnit;
  showPartnerSuggestions: boolean;
  ctaBarButtonConfig: ctaBarButtonConfigProps[];
  displayCtaBar: boolean;
  showOpeningHours: boolean;
  twoClickConsentRequired: boolean;
  twoClickData: {
    headline: string;
    copyText: string;
    toggleLabel: string;
    asideToggleHeadline: string;
    asideToggleLabel: string;
    asideToggleCopy: string;
    closeButton?: {
      text: string;
      redirectURL: string;
    };
  };
  fiveStarData: {
    displayFiveStarRating: boolean;
    legalText: string;
  };
  favoritePartner: {
    tenant: string;
    kvpsid: string;
  };
  myAudiCtaConfig: MyAudiBarButtonConfig | null;
  accessToken: string | null | undefined;
  selectedPartner: {
    tenant: string;
    kvpsid: string;
  };
  errorMessage: {
    code: string;
    message: string;
  };
  showModal: boolean;
  oneHeader: boolean;
  dealerContextService: undefined | DealerContextServiceV1;
  connectedDealer: string | null;
  connectDealer: boolean;
  autoConnectDealer: boolean;
  showComments: boolean;
  headerMobileView: boolean;
};

export enum ActionTypes {
  setQuery,
  setResults,
  setDealerRatings,
  setRatingSummary,
  setDealerReviews,
  setReviewCategoryFilter,
  setReviewStarFilter,
  setReviewSort,
  setHoveredDealer,
  setIsGoogleMapsApiLoaded,
  setIsAutocompletionOpen,
  setIsSidebarOpen,
  setIsFiltersOverlayOpen,
  setIsFlyoutOpen,
  setSearchText,
  setSearchRadius,
  addFilter,
  removeFilter,
  setFilters,
  setLoading,
  setSearchError,
  setSearchGeoLocation,
  setPlaceId,
  setNorthEastPosition,
  setSouthWestPosition,
  setUserLocationError,
  setUserGiveLocationPermission,
  setShowLocationError,
  resetGeolocationSearch,
  resetQuery,
  setUpdateMapZoomLevel,
  setSelectedDealerId,
  setShowRadius,
  setSearchRadiusExtended,
  setIsUserPositionUsed,
  setActiveMobileView,
  setLastSubmittedSearch,
  setMapZoom,
  setSearchInThisAreaVisible,
  setBounds,
  setMapCenterCoordinates,
  setUserGeoLocation,
  resetIsMapTriggeredByTeaser,
  setFavoritePartner,
  setMyAudiCtaConfig,
  setAccessToken,
  setSelectedPartner,
  setErrorMessage,
  setShowModal,
  setConnectedDealer,
}

type ActionPayloads = {
  [ActionTypes.setQuery]: string;
  [ActionTypes.setResults]: { dealers: Dealer[]; services: string[] };
  [ActionTypes.setDealerRatings]: { dealerRatings: { [key: string]: number } };
  [ActionTypes.setRatingSummary]: RatingSummary | null;
  [ActionTypes.setDealerReviews]: DealerReview[] | null;
  [ActionTypes.setReviewCategoryFilter]: ReviewCategory;
  [ActionTypes.setReviewStarFilter]: StarFilter;
  [ActionTypes.setReviewSort]: ReviewSort;
  [ActionTypes.setHoveredDealer]: Dealer | null;
  [ActionTypes.setIsGoogleMapsApiLoaded]: boolean;
  [ActionTypes.setIsAutocompletionOpen]: boolean;
  [ActionTypes.setIsSidebarOpen]: boolean;
  [ActionTypes.setIsFiltersOverlayOpen]: boolean;
  [ActionTypes.setIsFlyoutOpen]: boolean;
  [ActionTypes.setSearchText]: string;
  [ActionTypes.setSearchRadius]: number;
  [ActionTypes.addFilter]: string;
  [ActionTypes.removeFilter]: string;
  [ActionTypes.setFilters]: string[];
  [ActionTypes.setLoading]: boolean;
  [ActionTypes.setSearchError]: ApolloError | null;
  [ActionTypes.setSearchGeoLocation]: Coordinates;
  [ActionTypes.setPlaceId]: string;
  [ActionTypes.setNorthEastPosition]: Coordinates;
  [ActionTypes.setSouthWestPosition]: Coordinates;
  [ActionTypes.setUserLocationError]: boolean;
  [ActionTypes.setUserGiveLocationPermission]: boolean;
  [ActionTypes.setShowLocationError]: boolean;
  [ActionTypes.resetGeolocationSearch]: null;
  [ActionTypes.resetQuery]: null;
  [ActionTypes.setUpdateMapZoomLevel]: boolean;
  [ActionTypes.setSelectedDealerId]: string | null;
  [ActionTypes.setShowRadius]: boolean;
  [ActionTypes.setSearchRadiusExtended]: boolean;
  [ActionTypes.setIsUserPositionUsed]: boolean;
  [ActionTypes.setActiveMobileView]: 'map' | 'list';
  [ActionTypes.setLastSubmittedSearch]: string | null;
  [ActionTypes.setMapZoom]: number;
  [ActionTypes.setSearchInThisAreaVisible]: boolean;
  [ActionTypes.setBounds]: GoogleMapReact.Bounds;
  [ActionTypes.setMapCenterCoordinates]: Coordinates;
  [ActionTypes.setUserGeoLocation]: Coordinates | null;
  [ActionTypes.resetIsMapTriggeredByTeaser]: undefined;
  [ActionTypes.setFavoritePartner]: { tenant: string; kvpsid: string };
  [ActionTypes.setMyAudiCtaConfig]: MyAudiBarButtonConfig | null;
  [ActionTypes.setAccessToken]: string | null | undefined;
  [ActionTypes.setSelectedPartner]: { tenant: string; kvpsid: string };
  [ActionTypes.setErrorMessage]: { code: string; message: string };
  [ActionTypes.setShowModal]: boolean;
  [ActionTypes.setConnectedDealer]: string | null;
};

export type Action = {
  [K in ActionTypes]: {
    type: K;
    payload?: ActionPayloads[K];
  };
}[ActionTypes];

const reducer: Reducer<State, Action> = (state: State, action: Action) => {
  const { type, payload } = action;

  switch (type) {
    case ActionTypes.setQuery:
      return { ...state, query: payload };
    case ActionTypes.setResults:
      return {
        ...state,
        results: payload.dealers,
        currentResultsAvailableFilters: payload.services,
      };
    case ActionTypes.setDealerRatings:
      return { ...state, dealerRatings: payload.dealerRatings };
    case ActionTypes.setDealerReviews:
      return { ...state, dealerReviews: payload };
    case ActionTypes.setReviewCategoryFilter:
      return { ...state, reviewCategoryFilter: payload };
    case ActionTypes.setReviewStarFilter:
      return { ...state, reviewStarFilter: payload };
    case ActionTypes.setReviewSort:
      return { ...state, reviewSort: payload };
    case ActionTypes.setRatingSummary:
      return { ...state, ratingSummary: payload };
    case ActionTypes.setIsFlyoutOpen:
      return { ...state, isFlyoutOpen: payload };
    case ActionTypes.setHoveredDealer:
      return { ...state, hoveredDealer: payload };
    case ActionTypes.addFilter:
      return { ...state, userSelectedFilters: [...state.userSelectedFilters, payload] };
    case ActionTypes.removeFilter: {
      const newFilters = state.userSelectedFilters;
      const index = state.userSelectedFilters.indexOf(payload);
      if (index >= 0) {
        newFilters.splice(index, 1);
      }
      return { ...state, userSelectedFilters: [...newFilters] };
    }
    case ActionTypes.setFilters:
      return { ...state, userSelectedFilters: [...payload] as string[] };
    case ActionTypes.setIsSidebarOpen:
      return { ...state, isSidebarOpen: payload };
    case ActionTypes.setSearchText:
      return { ...state, searchText: payload };
    case ActionTypes.setSearchRadius:
      return { ...state, searchRadius: payload };
    case ActionTypes.setIsGoogleMapsApiLoaded:
      return { ...state, isGoogleMapsApiLoaded: payload };
    case ActionTypes.setIsAutocompletionOpen:
      return { ...state, isAutocompletionOpen: payload };
    case ActionTypes.setSearchError:
      return { ...state, searchError: payload };
    case ActionTypes.setNorthEastPosition:
      return { ...state, northEastPosition: payload };
    case ActionTypes.setSouthWestPosition:
      return { ...state, southWestPosition: payload };
    case ActionTypes.setLoading:
      return { ...state, isLoading: payload };
    case ActionTypes.setSearchGeoLocation:
      return { ...state, searchGeoLocation: payload };
    case ActionTypes.setPlaceId:
      return { ...state, placeId: payload };
    case ActionTypes.setUserLocationError:
      return { ...state, isUserLocationError: payload };
    case ActionTypes.setUserGiveLocationPermission:
      return { ...state, isUserLocationPermissionGiven: payload };
    case ActionTypes.setShowLocationError:
      return { ...state, showLocationError: payload };
    case ActionTypes.resetGeolocationSearch:
      return { ...state, searchGeoLocation: defaultState.searchGeoLocation };
    case ActionTypes.resetQuery:
      return { ...state, query: defaultState.query };
    case ActionTypes.setUpdateMapZoomLevel:
      return { ...state, updateMapZoomLevel: payload };
    case ActionTypes.setSelectedDealerId:
      return { ...state, selectedDealerId: payload };
    case ActionTypes.setShowRadius:
      return { ...state, showRadius: payload };
    case ActionTypes.setSearchRadiusExtended:
      return { ...state, searchRadiusExtended: payload };
    case ActionTypes.setIsUserPositionUsed:
      return { ...state, isUserPositionUsed: payload };
    case ActionTypes.setLastSubmittedSearch:
      return { ...state, lastSubmittedSearch: payload };
    case ActionTypes.setMapZoom:
      return { ...state, mapZoom: payload };
    case ActionTypes.setSearchInThisAreaVisible:
      return { ...state, searchInThisAreaVisible: payload };
    case ActionTypes.setBounds:
      return { ...state, bounds: payload };
    case ActionTypes.setMapCenterCoordinates:
      return { ...state, mapCenterCoordinates: payload };
    case ActionTypes.setUserGeoLocation:
      return { ...state, userGeolocation: payload };
    case ActionTypes.setIsFiltersOverlayOpen:
      return { ...state, isFiltersOverlayOpen: payload };
    case ActionTypes.setActiveMobileView:
      return { ...state, activeMobileView: payload };
    case ActionTypes.resetIsMapTriggeredByTeaser:
      return { ...state, isMapTriggeredByTeaser: false };
    case ActionTypes.setFavoritePartner:
      return { ...state, favoritePartner: payload };
    case ActionTypes.setMyAudiCtaConfig:
      return { ...state, myAudiCtaConfig: payload };
    case ActionTypes.setAccessToken:
      return { ...state, accessToken: payload };
    case ActionTypes.setSelectedPartner:
      return { ...state, selectedPartner: payload };
    case ActionTypes.setErrorMessage:
      return { ...state, errorMessage: payload };
    case ActionTypes.setShowModal:
      return { ...state, showModal: payload };
    case ActionTypes.setConnectedDealer:
      return { ...state, connectedDealer: payload };
    default:
      throw new Error(`Invalid action type: ${String(type)}`);
  }
};

export default reducer;
